import React, { useEffect, useRef, useState } from 'react';
import api from '../utils/api';
import AdminTabs from '../components/AdminTabs';
import { useAuth } from '../context/AuthContext';
import useAdminStats from '../hooks/useAdminStats';
import './AdminDashboard.css';

const STATUS_OPTIONS = ['placed', 'confirmed', 'processing', 'packed', 'shipped', 'out-for-delivery', 'delivered', 'cancelled', 'returned'];
// Orders grouped into tabs. Each tab fetches its set of statuses.
const ORDER_TABS = [
  { key: 'active',    label: 'Active',              statuses: ['placed', 'confirmed', 'processing', 'packed'] },
  { key: 'fulfilled', label: 'Shipped', statuses: ['out-for-delivery', 'shipped'] },
  { key: 'delivered', label: 'Delivered', statuses: ['delivered'] },
  { key: 'cancelled', label: 'Cancelled',           statuses: ['cancelled', 'returned'] },
  { key: 'all',       label: 'All',                 statuses: [] },
  // Not a filter over orders but a view of its own; it loads nothing from the
  // orders endpoint, so it carries no status list.
  { key: 'revenue',   label: 'Revenue by date',     statuses: [], view: 'summary' },
];

/* ── helpers ── */
const fmtDate = iso => new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
// shippingAddress may arrive as an object or a JSON string — normalize to an object.
const orderAddr = (order) => {
  const value = order?.shippingAddress;
  if (!value) return {};
  if (typeof value === 'object') return value;
  if (typeof value === 'string') { try { return JSON.parse(value) || {}; } catch { return {}; } }
  return {};
};

const money = n => '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
const SLA_DAYS = { india: 2, default: 7 };
const DAY_MS = 24 * 60 * 60 * 1000;
const DEAD_STATUSES = ['cancelled', 'returned'];
const dueByOf = (o) => new Date(new Date(o.createdAt).getTime()
  + (SLA_DAYS[String(o.shippingZone || '').toLowerCase()] ?? SLA_DAYS.default) * DAY_MS);

// Prefers the server's verdict; works it out here when the server has not sent
// one. A delivered order needs its status history to judge, and without history
// it is left alone rather than painted red on a guess.
const latenessOf = (o, now = new Date()) => {
  if (o.late !== undefined) return { late: o.late, daysLate: o.daysLate || 0, dueBy: o.dueBy };
  if (DEAD_STATUSES.includes(o.orderStatus)) return { late: false, daysLate: 0 };
  const due = dueByOf(o);
  if (o.orderStatus === 'delivered') {
    const hit = (o.statusHistory || []).find(h => h.status === 'delivered');
    if (!hit) return { late: false, daysLate: 0, dueBy: due };
    const over = new Date(hit.timestamp) - due;
    return { late: over > 0, daysLate: over > 0 ? Math.ceil(over / DAY_MS) : 0, dueBy: due };
  }
  const over = now - due;
  return { late: over > 0, daysLate: over > 0 ? Math.ceil(over / DAY_MS) : 0, dueBy: due };
};

// The day a sale belongs to, in IST — the business day the shop keeps.
const istDay = (d) => new Date(new Date(d).getTime() + 5.5 * 3600 * 1000).toISOString().slice(0, 10);

// Everything the summary panel shows, worked out from the orders already on
// screen. Only as complete as the list is — which is why it says so.
const summarise = (orders, days) => {
  // The window has to be applied here too. The server honours ?days=; this
  // fallback was summarising every order it had loaded, so picking 7 days still
  // showed last month.
  const cutoff = istDay(new Date(Date.now() - (Math.max(1, days) - 1) * DAY_MS));
  orders = orders.filter(o => istDay(o.createdAt) >= cutoff);
  const byDay = new Map();
  const totals = { orders: 0, revenue: 0, delivered: 0, deliveredRevenue: 0, late: 0, cancelled: 0 };
  for (const o of orders) {
    const key = istDay(o.createdAt);
    if (!byDay.has(key)) byDay.set(key, { date: key, orders: 0, revenue: 0, delivered: 0, deliveredRevenue: 0, late: 0, cancelled: 0 });
    const row = byDay.get(key);
    const value = Number(o.total || 0);
    const dead = DEAD_STATUSES.includes(o.orderStatus);
    row.orders += 1; totals.orders += 1;
    if (dead) { row.cancelled += 1; totals.cancelled += 1; }
    else { row.revenue += value; totals.revenue += value; }
    if (o.orderStatus === 'delivered') {
      row.delivered += 1; row.deliveredRevenue += value;
      totals.delivered += 1; totals.deliveredRevenue += value;
    }
    if (latenessOf(o).late) { row.late += 1; totals.late += 1; }
  }
  return {
    partial: true,
    days_window: days,
    sla: { india: SLA_DAYS.india, international: SLA_DAYS.default },
    totals,
    days: Array.from(byDay.values()).sort((a, b) => (a.date < b.date ? 1 : -1)),
  };
};

const dayLabel = (ymd) => new Date(`${ymd}T00:00:00`).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });

const ordersToCSV = orders => {
  const header = ['Order #', 'Date', 'Customer', 'Email', 'Items', 'Total', 'Payment', 'Status', 'Zone'];
  const rows = orders.map(o => [
    o.orderNumber,
    fmtDate(o.createdAt),
    o.user?.name || orderAddr(o).fullName || orderAddr(o).name || '',
    o.user?.email || orderAddr(o).email || o.guestEmail || '',
    o.items?.length ?? 0,
    o.total ?? 0,
    o.paymentStatus,
    o.orderStatus,
    o.shippingZone || '',
  ]);
  return [header, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
};

const downloadCSV = (content, filename) => {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

/* ── Order Detail Modal ── */
const OrderDetailModal = ({ order, onClose, onOrderUpdated }) => {
  const orderId = order._id || order.id;

  const [data, setData] = useState(null);
  const [loadingData, setLoadingData] = useState(true);
  const [products, setProducts] = useState([]);
  const [zones, setZones] = useState([]);

  const [newStatus, setNewStatus] = useState(order.orderStatus || '');
  const [trackingNumber, setTrackingNumber] = useState(order.trackingNumber || '');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [payStatus, setPayStatus] = useState(order.paymentStatus || 'pending');
  const [payMethod, setPayMethod] = useState(order.paymentMethod || 'razorpay');
  const [payRef, setPayRef] = useState(order.paymentId || '');
  const [savingPayment, setSavingPayment] = useState(false);

  const [editing, setEditing] = useState(false);
  const [editItems, setEditItems] = useState([]);
  const [pickProduct, setPickProduct] = useState('');
  const [pickWeight, setPickWeight] = useState('');
  const [savingItems, setSavingItems] = useState(false);

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get(`/orders/${orderId}`),
      api.get('/products?limit=200'),
      api.get('/shipping/zones'),
    ]).then(([oRes, pRes, zRes]) => {
      if (!alive) return;
      const full = oRes.data.order;
      setData(full);
      setNewStatus(full.orderStatus || '');
      setTrackingNumber(full.trackingNumber || '');
      setPayStatus(full.paymentStatus || 'pending');
      setPayMethod(full.paymentMethod || 'razorpay');
      setPayRef(full.paymentId || '');
      setProducts(pRes.data.products || []);
      setZones(zRes.data.zones || []);
    }).catch(console.error).finally(() => { if (alive) setLoadingData(false); });
    return () => { alive = false; };
  }, [orderId]);

  const savePayment = async () => {
    setSavingPayment(true);
    try {
      const res = await api.put(`/orders/${orderId}/payment`, {
        paymentStatus: payStatus,
        paymentMethod: payMethod,
        paymentId: payRef,
      });
      const u = res.data.order;
      setData(prev => ({
        ...prev,
        paymentStatus: u.paymentStatus,
        paymentMethod: u.paymentMethod,
        paymentId: u.paymentId,
        statusHistory: u.statusHistory,
      }));
      onOrderUpdated(orderId, { paymentStatus: u.paymentStatus });
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update payment');
    } finally {
      setSavingPayment(false);
    }
  };

  const handleUpdateStatus = async e => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await api.put(`/orders/${orderId}/status`, { status: newStatus, trackingNumber, note });
      const u = res.data.order;
      setData(prev => ({ ...prev, orderStatus: u.orderStatus, trackingNumber: u.trackingNumber, statusHistory: u.statusHistory }));
      onOrderUpdated(orderId, { orderStatus: u.orderStatus, trackingNumber: u.trackingNumber });
      setNote('');
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update status');
    } finally {
      setSubmitting(false);
    }
  };

  /* ── item editing ── */
  const startEditing = () => {
    setEditItems((data.items || []).map(i => ({
      id: i.id,
      productId: i.productId,
      name: i.name,
      weight: i.variantWeight,
      variantPrice: Number(i.variantPrice),
      quantity: i.quantity,
    })));
    setPickProduct(''); setPickWeight('');
    setEditing(true);
  };

  const setQty = (idx, qty) => setEditItems(prev =>
    prev.map((it, i) => (i === idx ? { ...it, quantity: Math.max(1, qty || 1) } : it)));

  const removeItem = idx => setEditItems(prev => prev.filter((_, i) => i !== idx));

  const addItem = () => {
    const p = products.find(x => String(x._id) === String(pickProduct));
    const v = p && (p.variants || []).find(x => x.weight === pickWeight);
    if (!p || !v) return;
    setEditItems(prev => {
      const idx = prev.findIndex(it => String(it.productId) === String(p._id) && it.weight === v.weight);
      if (idx >= 0) {
        const c = [...prev];
        c[idx] = { ...c[idx], quantity: c[idx].quantity + 1 };
        return c;
      }
      return [...prev, { productId: p._id, name: p.name, weight: v.weight, variantPrice: Number(v.price), quantity: 1 }];
    });
    setPickProduct(''); setPickWeight('');
  };

  const saveItems = async () => {
    if (editItems.length === 0) { alert('An order must have at least one item.'); return; }
    setSavingItems(true);
    try {
      const res = await api.put(`/orders/${orderId}`, {
        items: editItems.map(it => ({ id: it.id, productId: it.productId, weight: it.weight, quantity: it.quantity })),
      });
      const updated = res.data.order;
      setData(updated);
      setEditing(false);
      onOrderUpdated(orderId, updated);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update order items');
    } finally {
      setSavingItems(false);
    }
  };

  /* ── live recalculation (mirrors the server) ── */
  const calcShipping = sub => {
    const zone = zones.find(z => z.id === data?.shippingZone);
    if (!zone) return Number(data?.shippingCost) || 0;
    const methodId = data?.shippingMethod === 'express' ? 'express' : 'standard';
    const m = zone.methods.find(x => x.id === methodId) || zone.methods[0];
    let cost = m?.rate || 0;
    if (zone.id === 'india' && zone.freeAbove && sub >= zone.freeAbove) cost = 0;
    return cost;
  };

  const editSubtotal = editItems.reduce((s, it) => s + it.variantPrice * it.quantity, 0);
  const editDiscount = Number(data?.discount) || 0;
  const editTax      = Number(data?.tax) || 0;
  const editShipping = calcShipping(editSubtotal);
  const editTotal    = editSubtotal - editDiscount + editTax + editShipping;
  const originalTotal = Number(data?.total) || 0;
  const diff = editTotal - originalTotal;

  const pickProductObj = products.find(x => String(x._id) === String(pickProduct));
  const pickWeights = (pickProductObj?.variants || []).filter(v => v.price);

  // JSON columns can come back as objects OR JSON strings — parse defensively.
  const parseMaybeJson = (value) => {
    if (!value) return null;
    if (typeof value === 'object') return value;
    if (typeof value === 'string') {
      try { return JSON.parse(value); } catch { return null; }
    }
    return null;
  };
  const addr = parseMaybeJson(data?.shippingAddress || order.shippingAddress) || {};
  const billAddr = parseMaybeJson(data?.billingAddress || order.billingAddress);
  const history = data?.statusHistory || [];
  const customer = {
    name: data?.user?.name || order.user?.name || addr.fullName || addr.name || '—',
    email: data?.user?.email || order.user?.email || addr.email || data?.guestEmail || order.guestEmail || '—',
    phone: data?.user?.phone || addr.phone || '—',
  };

  return (
    <div className="order-modal-overlay" onClick={onClose}>
      <div className="order-modal-card" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="order-modal-header">
          <div>
            <h2 className="order-modal-title">Order #{order.orderNumber}</h2>
            <p className="order-modal-date">{fmtDate(order.createdAt)}</p>
          </div>
          <button className="modal-close-btn" onClick={onClose}>✕</button>
        </div>

        {loadingData || !data ? (
          <div className="order-modal-body" style={{ textAlign: 'center', padding: '3rem' }}>
            <div className="loading-spinner" style={{ margin: '0 auto' }} />
          </div>
        ) : (
        <div className="order-modal-body">
          {/* Customer */}
          <section className="omd-section">
            <h3 className="omd-section-title">Customer</h3>
            <p className="omd-line"><strong>{data.user?.name || order.user?.name || addr.fullName || addr.name || '—'}</strong></p>
            <p className="omd-line omd-muted">{data.user?.email || order.user?.email || addr.email || '—'}</p>
          </section>

          {/* Items */}
          <section className="omd-section">
            <div className="omd-section-head">
              <h3 className="omd-section-title">Items</h3>
              {!editing && (
                <button className="omd-edit-toggle" onClick={startEditing}>Edit Items</button>
              )}
            </div>

            {!editing ? (
              <>
                <table className="omd-items-table">
                  <thead>
                    <tr><th>Product</th><th>Weight</th><th>Qty</th><th>Price</th></tr>
                  </thead>
                  <tbody>
                    {(data.items || []).map((item, i) => (
                      <tr key={i}>
                        <td>
                          {item.name || item.product?.name || '—'}
                          {item.bundleType === 'hamper' && (
                            <div className="omd-hamper-detail">
                              <strong>Custom Gift Hamper</strong>
                              {item.customization?.styleInstructions && <span>Style: {item.customization.styleInstructions}</span>}
                              {item.customization?.personalMessage && <span>Message: {item.customization.personalMessage}</span>}
                            </div>
                          )}
                        </td>
                        <td>{item.variantWeight || '—'}</td>
                        <td>{item.quantity}</td>
                        <td>{money(item.price)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="omd-totals">
                  <div className="omd-totals-row"><span>Subtotal</span><span>{money(data.subtotal)}</span></div>
                  {editDiscount > 0 && (
                    <div className="omd-totals-row"><span>Discount</span><span>−{money(editDiscount)}</span></div>
                  )}
                  {editTax > 0 && (
                    <div className="omd-totals-row"><span>Tax</span><span>{money(editTax)}</span></div>
                  )}
                  <div className="omd-totals-row"><span>Shipping</span><span>{money(data.shippingCost)}</span></div>
                  <div className="omd-totals-row omd-totals-grand"><span>Total</span><span>{money(data.total)}</span></div>
                </div>
              </>
            ) : (
              <>
                <table className="omd-items-table omd-items-edit">
                  <thead>
                    <tr><th>Product</th><th>Weight</th><th>Unit</th><th>Qty</th><th>Line</th><th></th></tr>
                  </thead>
                  <tbody>
                    {editItems.map((it, i) => (
                      <tr key={`${it.productId}-${it.weight}-${i}`}>
                        <td>{it.name}</td>
                        <td>{it.weight}</td>
                        <td>{money(it.variantPrice)}</td>
                        <td>
                          <input
                            type="number" min="1" className="omd-qty-input"
                            value={it.quantity}
                            onChange={e => setQty(i, parseInt(e.target.value))}
                          />
                        </td>
                        <td>{money(it.variantPrice * it.quantity)}</td>
                        <td>
                          <button className="omd-item-remove" title="Remove"
                            onClick={() => removeItem(i)}>✕</button>
                        </td>
                      </tr>
                    ))}
                    {editItems.length === 0 && (
                      <tr><td colSpan={6} className="omd-empty-items">No items — add at least one.</td></tr>
                    )}
                  </tbody>
                </table>

                {/* Add product */}
                <div className="omd-add-row">
                  <select
                    className="omd-add-select"
                    value={pickProduct}
                    onChange={e => { setPickProduct(e.target.value); setPickWeight(''); }}
                  >
                    <option value="">Add a product…</option>
                    {products.map(p => (
                      <option key={p._id} value={p._id}>{p.name}</option>
                    ))}
                  </select>
                  <select
                    className="omd-add-select"
                    value={pickWeight}
                    disabled={!pickProductObj}
                    onChange={e => setPickWeight(e.target.value)}
                  >
                    <option value="">Weight…</option>
                    {pickWeights.map(v => (
                      <option key={v.weight} value={v.weight}>{v.weight} — {money(v.price)}</option>
                    ))}
                  </select>
                  <button
                    className="btn btn-primary btn-sm"
                    disabled={!pickProduct || !pickWeight}
                    onClick={addItem}
                  >
                    + Add
                  </button>
                </div>

                {/* Live totals + difference */}
                <div className="omd-totals">
                  <div className="omd-totals-row"><span>Subtotal</span><span>{money(editSubtotal)}</span></div>
                  {editDiscount > 0 && (
                    <div className="omd-totals-row"><span>Discount</span><span>−{money(editDiscount)}</span></div>
                  )}
                  {editTax > 0 && (
                    <div className="omd-totals-row"><span>Tax</span><span>{money(editTax)}</span></div>
                  )}
                  <div className="omd-totals-row">
                    <span>Shipping {editShipping === 0 ? '(free)' : ''}</span>
                    <span>{money(editShipping)}</span>
                  </div>
                  <div className="omd-totals-row omd-totals-grand"><span>New Total</span><span>{money(editTotal)}</span></div>
                  <div className="omd-totals-row omd-totals-old"><span>Original Total</span><span>{money(originalTotal)}</span></div>
                  <div className={`omd-totals-row omd-diff ${diff > 0 ? 'up' : diff < 0 ? 'down' : ''}`}>
                    <span>Difference</span>
                    <span>{diff > 0 ? '+' : diff < 0 ? '−' : ''}{money(Math.abs(diff))}</span>
                  </div>
                </div>

                <div className="omd-edit-actions">
                  <button className="btn btn-outline btn-sm" onClick={() => setEditing(false)} disabled={savingItems}>
                    Cancel
                  </button>
                  <button className="btn btn-primary btn-sm" onClick={saveItems} disabled={savingItems}>
                    {savingItems ? 'Saving…' : 'Save Changes'}
                  </button>
                </div>
              </>
            )}
          </section>

          {/* Customer */}
          <section className="omd-section">
            <h3 className="omd-section-title">Customer</h3>
            <p className="omd-line">{customer.name}</p>
            <p className="omd-line omd-muted">{customer.email}</p>
            <p className="omd-line omd-muted">{customer.phone}</p>
          </section>

          {/* Shipping Address */}
          <section className="omd-section">
            <h3 className="omd-section-title">Shipping Address</h3>
            <p className="omd-line">{addr.fullName || addr.name}</p>
            {addr.line1 && <p className="omd-line">{addr.line1}</p>}
            {addr.line2 && <p className="omd-line">{addr.line2}</p>}
            <p className="omd-line">
              {[addr.city, addr.state, addr.pincode, addr.country].filter(Boolean).join(', ')}
            </p>
            {addr.phone && <p className="omd-line omd-muted">{addr.phone}</p>}
          </section>

          {/* Billing Address — only when different from shipping */}
          <section className="omd-section">
            <h3 className="omd-section-title">Billing Address</h3>
            {billAddr ? (
              <>
                <p className="omd-line">{billAddr.fullName || billAddr.name}</p>
                {billAddr.line1 && <p className="omd-line">{billAddr.line1}</p>}
                {billAddr.line2 && <p className="omd-line">{billAddr.line2}</p>}
                <p className="omd-line">
                  {[billAddr.city, billAddr.state, billAddr.pincode, billAddr.country].filter(Boolean).join(', ')}
                </p>
                {billAddr.phone && <p className="omd-line omd-muted">{billAddr.phone}</p>}
              </>
            ) : (
              <p className="omd-line omd-muted">Same as shipping address</p>
            )}
          </section>

          {/* Payment — editable */}
          <section className="omd-section">
            <h3 className="omd-section-title">Payment</h3>
            <div className="omd-form-row">
              <div className="omd-form-group">
                <label>Payment Status</label>
                <select value={payStatus} onChange={e => setPayStatus(e.target.value)}>
                  {['pending', 'paid', 'failed', 'refunded'].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="omd-form-group">
                <label>Method</label>
                <select value={payMethod} onChange={e => setPayMethod(e.target.value)}>
                  {['razorpay', 'icici', 'upi', 'cod', 'cash', 'bank_transfer'].map(m => (
                    <option key={m} value={m}>{m === 'bank_transfer' ? 'Bank Transfer' : m.toUpperCase()}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="omd-form-group">
              <label>Reference / Transaction ID</label>
              <input
                type="text"
                value={payRef}
                onChange={e => setPayRef(e.target.value)}
                placeholder="e.g. UPI ref, bank txn no., receipt no."
              />
            </div>
            <button className="btn btn-primary btn-sm" onClick={savePayment} disabled={savingPayment}>
              {savingPayment ? 'Saving…' : 'Save Payment Details'}
            </button>
          </section>

          {/* Current order status */}
          <section className="omd-section">
            <h3 className="omd-section-title">Current Status</h3>
            <span className={`order-status-badge status-${data.orderStatus}`}>{data.orderStatus}</span>
            {data.trackingNumber && (
              <p className="omd-line omd-muted" style={{ marginTop: '0.5rem' }}>
                Tracking: {data.trackingNumber}
              </p>
            )}
          </section>

          {/* Status Update */}
          <section className="omd-section">
            <h3 className="omd-section-title">Update Status</h3>
            <form className="omd-status-form" onSubmit={handleUpdateStatus}>
              <div className="omd-form-row">
                <div className="omd-form-group">
                  <label>New Status</label>
                  <select value={newStatus} onChange={e => setNewStatus(e.target.value)} required>
                    {STATUS_OPTIONS.map(s => (
                      <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1).replace(/-/g, ' ')}</option>
                    ))}
                  </select>
                </div>
                <div className="omd-form-group">
                  <label>Tracking Number</label>
                  <input
                    type="text"
                    placeholder="Optional"
                    value={trackingNumber}
                    onChange={e => setTrackingNumber(e.target.value)}
                  />
                </div>
              </div>
              <div className="omd-form-group">
                <label>Note (internal)</label>
                <textarea
                  rows={2}
                  placeholder="Optional internal note"
                  value={note}
                  onChange={e => setNote(e.target.value)}
                />
              </div>
              <div className="omd-form-actions">
                <button type="submit" className="btn btn-primary btn-sm" disabled={submitting}>
                  {submitting ? 'Updating…' : 'Update Status'}
                </button>
              </div>
            </form>
          </section>

          {/* Status History */}
          {history.length > 0 && (
            <section className="omd-section">
              <h3 className="omd-section-title">Status History</h3>
              <div className="omd-timeline">
                {history.map((h, i) => (
                  <div key={i} className="omd-timeline-item">
                    <div className="omd-timeline-dot" />
                    <div>
                      <span className={`order-status-badge status-${h.status}`}>{h.status}</span>
                      <span className="omd-timeline-date">{fmtDate(h.timestamp || h.changedAt || h.date)}</span>
                      {h.note && <p className="omd-timeline-note">{h.note}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
        )}
      </div>
    </div>
  );
};

/* ── Main Component ── */
const AdminDashboard = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const isSuperAdmin = user?.role === 'super_admin';   // financials + payment settings


  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [statusFilter, setStatusFilter] = useState('active');
  const [payMethods, setPayMethods] = useState(null);
  const { stats } = useAdminStats(isAdmin);   // shared with the menu badges
  const [summary, setSummary] = useState(null);
  const [summaryDays, setSummaryDays] = useState(14);
  const [search, setSearch] = useState('');
  const [priority, setPriority] = useState('all');
  const [dateRange, setDateRange] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sort, setSort] = useState('createdAt:desc');
  const [selectedIds, setSelectedIds] = useState([]);
  const searchInput = useRef(null);
  const [ordersError, setOrdersError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const ordersRequest = useRef(0);

  const togglePayMethod = async (key) => {
    if (!payMethods) return;
    const prev = payMethods;
    const next = { ...payMethods, [key]: !payMethods[key] };
    setPayMethods(next); // optimistic
    try {
      const res = await api.put('/settings/payment-methods', next);
      setPayMethods(res.data.methods);
    } catch (err) {
      setPayMethods(prev); // revert on failure
      alert(err.response?.data?.message || 'Could not update payment methods');
    }
  };

  const fetchOrders = async (request) => {
    const rows = [];
    let nextPage = 1;
    let total = Infinity;
    while (rows.length < total) {
      const { data } = await api.get(`/orders?limit=100&page=${nextPage}`);
      if (request !== ordersRequest.current) return [];
      const batch = data.orders || [];
      rows.push(...batch);
      total = Number(data.total ?? rows.length);
      if (!batch.length) break;
      nextPage += 1;
    }
    return rows;
  };

  useEffect(() => {
    // Store managers only see orders — the stats endpoint is admin-only.
    // Payment settings are super-admin only.
    if (isSuperAdmin) {
      api.get('/settings/payment-methods')
        .then(res => setPayMethods(res.data.methods))
        .catch(console.error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, isSuperAdmin]);

  useEffect(() => {
    const request = ++ordersRequest.current;
    setLoading(true);
    setOrdersError('');
    fetchOrders(request)
      .then(orders => { if (request === ordersRequest.current) setRecentOrders(orders); })
      .catch(() => { if (request === ordersRequest.current) setOrdersError('Orders could not be loaded. Please try again.'); })
      .finally(() => { if (request === ordersRequest.current) setLoading(false); });
    return () => { ordersRequest.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  useEffect(() => { setPage(1); setSelectedIds([]); }, [search, priority, dateRange, statusFilter, pageSize, sort, refreshKey]);
  useEffect(() => {
    const focusSearch = event => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k' && !selectedOrder && searchInput.current) {
        event.preventDefault(); searchInput.current.focus();
      }
    };
    window.addEventListener('keydown', focusSearch);
    return () => window.removeEventListener('keydown', focusSearch);
  }, [selectedOrder]);

  // The period picker gets an effect of its own. It was sharing the one above,
  // so changing it re-ran everything — including a fetch of the ACTIVE orders,
  // which quietly threw away whichever tab you were looking at and left the
  // fallback summarising the wrong set.
  //
  // Revenue is an owner's question; a store manager never asks for it and the
  // endpoint refuses them anyway.
  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    api.get(`/orders/summary?days=${summaryDays}`)
      .then(res => { if (!cancelled) setSummary(res.data); })
      // No endpoint yet: the panel falls back to the orders already loaded.
      .catch(() => { if (!cancelled) setSummary(null); });
    // A slow answer for 90 days must not land after a later click on 7.
    return () => { cancelled = true; };
  }, [isAdmin, summaryDays]);

  const handleFilterChange = filter => {
    setStatusFilter(filter);
    setPriority('all');
  };

  const handleStatusUpdated = (orderId, updatedOrder) => {
    setRefreshKey(key => key + 1);
  };

  const isOverdue = order => ![...DEAD_STATUSES, 'delivered'].includes(order.orderStatus) && latenessOf(order).late;
  const visibleOrders = recentOrders.filter(order => {
    const tab = ORDER_TABS.find(t => t.key === statusFilter);
    if (tab?.statuses.length && !tab.statuses.includes(order.orderStatus)) return false;
    if (dateRange !== 'all' && istDay(order.createdAt) < istDay(new Date(Date.now() - (Number(dateRange) - 1) * DAY_MS))) return false;
    const address = orderAddr(order);
    const matchesSearch = [order.orderNumber, order.user?.name, order.user?.email, address.fullName, address.name, address.phone, address.city, order.guestEmail]
      .some(value => String(value || '').toLowerCase().includes(search.trim().toLowerCase().replace(/^#/, '')));
    return matchesSearch && (priority === 'all' || (priority === 'late' ? isOverdue(order) : order.paymentStatus === 'pending'));
  });
  const [sortField, sortDirection] = sort.split(':');
  const sortValue = order => sortField === 'customer' ? (order.user?.name || orderAddr(order).fullName || orderAddr(order).name || '')
    : sortField === 'address' ? [orderAddr(order).city, orderAddr(order).line1].filter(Boolean).join(' ')
    : sortField === 'total' ? Number(order.total || 0) : order[sortField] || '';
  visibleOrders.sort((a, b) => {
    const left = sortValue(a), right = sortValue(b);
    const comparison = sortField === 'total' ? left - right : String(left).localeCompare(String(right), 'en', { numeric: true });
    return sortDirection === 'asc' ? comparison : -comparison;
  });
  const toggleSort = field => setSort(`${field}:${sortField === field && sortDirection === 'asc' ? 'desc' : 'asc'}`);
  const orderKey = order => String(order._id ?? order.id);
  const toggleSelection = order => setSelectedIds(ids => ids.includes(orderKey(order)) ? ids.filter(id => id !== orderKey(order)) : [...ids, orderKey(order)]);
  const pageCount = Math.max(1, Math.ceil(visibleOrders.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const pagedOrders = visibleOrders.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const countFor = tab => recentOrders.filter(o => !tab.statuses.length || tab.statuses.includes(o.orderStatus)).length;
  const allPageSelected = pagedOrders.length > 0 && pagedOrders.every(order => selectedIds.includes(orderKey(order)));

  // The server's figures cover every order in the window; the fallback covers
  // only what is loaded. Either way the panel has something to show.
  const view = !isAdmin ? null : (summary || (recentOrders.length ? summarise(recentOrders, summaryDays) : null));

  const handleExportCSV = () => {
    const csv = ordersToCSV(selectedIds.length ? visibleOrders.filter(order => selectedIds.includes(orderKey(order))) : visibleOrders);
    const label = statusFilter === 'all' ? 'all' : statusFilter;
    downloadCSV(csv, `orders-${label}-${Date.now()}.csv`);
  };

  return (
    <div className="admin-page orders-workspace">
      <div className="container">
        <AdminTabs />


        {/* Orders, products and customers are counted on the menu items they
            belong to now. Revenue is not a count of anything and has no menu
            item to sit on, so it stays here — as one line rather than a grid of
            tiles pushing the day's work below the fold. Super admin only. */}
        {isSuperAdmin && (
          <p className="admin-revenue-line">
            Revenue to date <strong>₹{(stats?.totalRevenue || 0).toLocaleString('en-IN')}</strong>
          </p>
        )}

        {/* Payment Methods — super admin only */}
        {isSuperAdmin && payMethods && (
          <details className="admin-section payment-settings">
            <summary>Checkout payment settings <span>Manage available payment methods</span></summary>
            <p className="payment-settings-sub">Choose which payment options customers can use at checkout.</p>
            <div className="payment-toggle-list">
              {[
                ['razorpay', 'Online Payment', 'Cards, UPI & netbanking via Razorpay'],
                ['cod', 'Cash on Delivery', 'Customer pays when the order is delivered'],
                ['upi', 'UPI (direct)', 'Pay-to-UPI with manual confirmation'],
              ].map(([key, label, desc]) => (
                <label key={key} className={`payment-toggle${payMethods[key] ? ' on' : ''}`}>
                  <div className="payment-toggle-text">
                    <strong>{label}</strong>
                    <span>{desc}</span>
                  </div>
                  <input
                    type="checkbox"
                    checked={!!payMethods[key]}
                    onChange={() => togglePayMethod(key)}
                  />
                </label>
              ))}
            </div>
          </details>
        )}

        {/* Recent Orders */}
        <div className="admin-section orders-panel">
          <div className={`order-filterbar${statusFilter === 'revenue' ? ' order-filterbar--summary' : ''}`}>
            {statusFilter !== 'revenue' && <label className="order-filter-search">
              <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5" /><path d="m16 16 4 4" /></svg>
              <input ref={searchInput} type="search" aria-label="Search orders by number, customer, phone or city" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search order number, customer name, phone, email or city…" />
              <span className="order-search-shortcut" aria-hidden="true"><kbd>Ctrl</kbd><kbd>K</kbd></span>
            </label>}
            <label className="order-filter-field"><span>Order view</span>
              <select value={statusFilter} onChange={e => handleFilterChange(e.target.value)}>
                {ORDER_TABS.filter(tab => tab.view !== 'summary' || isAdmin).map(tab => (
                  <option key={tab.key} value={tab.key}>{tab.label}{tab.view !== 'summary' && !loading && !ordersError ? ` (${countFor(tab)})` : ''}</option>
                ))}
              </select>
            </label>
          {statusFilter !== 'revenue' && <>
            <label className={`order-filter-field${priority !== 'all' ? ' is-filtered' : ''}`}><span>Attention</span>
              <select value={priority} onChange={e => setPriority(e.target.value)}>
                <option value="all">Any priority</option>
                <option value="late">Overdue delivery</option>
                <option value="pending">Payment pending</option>
              </select>
            </label>
            <label className={`order-filter-field${dateRange !== 'all' ? ' is-filtered' : ''}`}><span>Placed</span>
              <select value={dateRange} onChange={e => setDateRange(e.target.value)}><option value="all">Any date</option><option value="1">Today</option><option value="7">Last 7 days</option><option value="30">Last 30 days</option></select>
            </label>
          <div className="orders-header-actions order-filter-actions">
            <button className="orders-icon-button" aria-label="Refresh orders" title="Refresh orders" disabled={loading || statusFilter === 'revenue'} onClick={() => setRefreshKey(key => key + 1)}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false"><path d="M20 7v5h-5M4 17v-5h5" /><path d="M6.1 6.1A8 8 0 0 1 19.5 10L20 12M4 12l.5 2A8 8 0 0 0 17.9 17.9" /></svg>
            </button>
            {statusFilter !== 'revenue' && <button className="orders-icon-button" aria-label={selectedIds.length ? 'Export selected orders as CSV' : 'Export filtered orders as CSV'} title={selectedIds.length ? 'Export selected orders' : 'Export filtered orders'} disabled={loading || !!ordersError || !visibleOrders.length} onClick={handleExportCSV}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false"><path d="M12 3v12m-5-5 5 5 5-5M5 16v4h14v-4" /></svg>
            </button>}
          </div>
          </>}
          {/* The chips row restated the controls directly above it — every filter
              already shows its own setting, and each select can be put back to
              "any" where it stands. */}
          </div>

          {statusFilter !== 'revenue' && ordersError && <div className="orders-feedback" role="alert">{ordersError} <button className="btn-export-csv" onClick={() => setRefreshKey(key => key + 1)}>Try again</button></div>}
          {statusFilter !== 'revenue' && loading && <div className="orders-feedback" role="status">Loading your order queue…</div>}
          {statusFilter === 'revenue' && isAdmin && !view && <div className="orders-feedback">No revenue summary is available.</div>}

          {statusFilter === 'revenue' && isAdmin && view && (
            <section className="rev-panel">
              <div className="rev-head">
                <h3>Revenue by date</h3>
                <div className="rev-head-right">
                  <select value={summaryDays} onChange={e => setSummaryDays(Number(e.target.value))}>
                    <option value={7}>Last 7 days</option>
                    <option value={14}>Last 14 days</option>
                    <option value={30}>Last 30 days</option>
                    <option value={90}>Last 90 days</option>
                  </select>
                  <span className="rev-sla">
                    Late = past {view.sla?.india}d in India, {view.sla?.international}d international
                  </span>
                </div>
              </div>

              {view.partial && (
                <p className="rev-partial">
                  Counted from the orders currently loaded that fall in the last {summaryDays} days, not
                  every order in that window —
                  the server's own figures will appear once the API is updated.
                </p>
              )}
              <div className="rev-totals">
                <div className="rev-stat"><span>Revenue</span><strong>{money(view.totals.revenue)}</strong>
                  <em>{view.totals.orders} orders</em></div>
                <div className="rev-stat"><span>Delivered</span><strong>{money(view.totals.deliveredRevenue)}</strong>
                  <em>{view.totals.delivered} of {view.totals.orders} orders</em></div>
                <div className="rev-stat"><span>Yet to arrive</span>
                  <strong>{money(view.totals.revenue - view.totals.deliveredRevenue)}</strong>
                  <em>{Math.max(0, view.totals.orders - view.totals.delivered - view.totals.cancelled)} orders</em></div>
                <div className={`rev-stat${view.totals.late ? ' rev-stat--late' : ''}`}><span>Running late</span>
                  <strong>{view.totals.late}</strong>
                  <em>{view.totals.late ? 'past the promised window' : 'nothing overdue'}</em></div>
              </div>

              <div className="admin-table-wrap rev-days">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th className="num">Orders</th>
                      <th className="num">Revenue</th>
                      <th className="num">Delivered</th>
                      <th className="num">Delivered value</th>
                      <th className="num">Late</th>
                    </tr>
                  </thead>
                  <tbody>
                    {view.days.map(d => (
                      <tr key={d.date} className={d.late ? 'row-late' : ''}>
                        <td>{dayLabel(d.date)}</td>
                        <td className="num">{d.orders}{d.cancelled ? <span className="cell-sub"> · {d.cancelled} cancelled</span> : null}</td>
                        <td className="num"><strong>{money(d.revenue)}</strong></td>
                        <td className="num">{d.delivered}</td>
                        <td className="num">{money(d.deliveredRevenue)}</td>
                        <td className="num">{d.late ? <span className="late-pill">{d.late}</span> : '—'}</td>
                      </tr>
                    ))}
                    {!view.days.length && (
                      <tr><td colSpan={6} className="rev-empty">No orders in this period.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {(statusFilter !== 'revenue' || !isAdmin) && !loading && !ordersError && (
          <div className="orders-table-card">
          <div className="order-filter-results">
            <h2 className="orders-result-count" role="status">{visibleOrders.length} {statusFilter === 'all' ? '' : (ORDER_TABS.find(t => t.key === statusFilter)?.label || '').toLowerCase() + ' '}orders</h2>
            {selectedIds.length > 0 ? <button className="orders-selection-export" onClick={handleExportCSV}>Export {selectedIds.length} selected</button> : <span className="orders-sort-hint">{sort === 'createdAt:desc' ? 'Showing latest orders' : 'Sorted orders'}</span>}
            <label className="orders-sort">Sort by:
              <select value={sort} onChange={e => setSort(e.target.value)}>
                <option value="createdAt:desc">Latest</option><option value="createdAt:asc">Oldest</option><option value="total:desc">Highest total</option><option value="total:asc">Lowest total</option>
                {!['createdAt:desc', 'createdAt:asc', 'total:desc', 'total:asc'].includes(sort) && <option value={sort}>Column: {sortField} ({sortDirection})</option>}
              </select>
            </label>
          </div>
          <div className="admin-table-wrap">
            <table className="admin-table orders-reference-table">
              <thead>
                <tr>
                  <th className="order-check-cell"><input type="checkbox" aria-label="Select all orders on this page" checked={allPageSelected} ref={node => { if (node) node.indeterminate = !allPageSelected && pagedOrders.some(order => selectedIds.includes(orderKey(order))); }} onChange={() => setSelectedIds(ids => allPageSelected ? ids.filter(id => !pagedOrders.some(order => orderKey(order) === id)) : [...new Set([...ids, ...pagedOrders.map(orderKey)])])} /></th>
                  {[['orderNumber', 'Order #'], ['customer', 'Customer'], ['address', 'Address'], ['total', 'Total'], ['shippingZone', 'Zone'], ['paymentStatus', 'Payment'], ['orderStatus', 'Status'], ['createdAt', 'Date']].map(([field, label]) => <th key={field} aria-sort={sortField === field ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}><button className="order-column-sort" onClick={() => toggleSort(field)}>{label}<span aria-hidden="true">{sortField === field ? (sortDirection === 'asc' ? '↑' : '↓') : '↕'}</span></button></th>)}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagedOrders.map(order => {
                  const late = latenessOf(order);
                  return (
                  <tr
                    key={orderKey(order)}
                    className={`order-row-clickable${late.late ? ' order-row-late' : ''}`}
                    onClick={() => setSelectedOrder(order)}
                    title={late.late
                      ? `${late.daysLate} day${late.daysLate === 1 ? '' : 's'} past the promised delivery window`
                      : 'Click to view details'}
                  >
                    <td className="order-check-cell" onClick={e => e.stopPropagation()}><input type="checkbox" aria-label={`Select order ${order.orderNumber}`} checked={selectedIds.includes(orderKey(order))} onChange={() => toggleSelection(order)} /></td>
                    <td><button className="order-open" onClick={e => { e.stopPropagation(); setSelectedOrder(order); }} aria-label={`View order ${order.orderNumber}`}>#{order.orderNumber}</button>
                      {late.late && <span className="late-pill" title={late.dueBy ? `Due by ${fmtDate(late.dueBy)}` : ''}>
                        {late.daysLate}d late
                      </span>}
                    </td>
                    <td>
                      <div className="customer-cell">
                        <span>{order.user?.name || orderAddr(order).fullName || orderAddr(order).name || '—'}</span>
                        <span className="cell-sub">{orderAddr(order).phone || ''}</span>
                        <span className="cell-sub">{order.user?.email || orderAddr(order).email || order.guestEmail || ''}</span>
                      </div>
                    </td>
                    <td>
                      {(() => {
                        const a = orderAddr(order);
                        const street = [a.line1, a.line2].filter(Boolean).join(', ');
                        const locality = [a.city, a.state, a.pincode].filter(Boolean).join(', ');
                        return (
                          <div className="address-cell">
                            <span>{street || locality || '—'}</span>
                            {street && locality && <span className="cell-sub">{locality}</span>}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="order-total-cell"><strong>{Number(order.total || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 })}</strong></td>
                    <td>
                      <span className="zone-badge">{order.shippingZone?.toUpperCase()}</span>
                    </td>
                    <td>
                      <span className={`payment-status ${order.paymentStatus}`}>
                        {order.paymentStatus}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`order-status-badge status-${order.orderStatus}`}
                      >
                        {order.orderStatus}
                      </span>
                    </td>
                    <td><div className="cell-date">
                      <span>{new Date(order.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                      <span className="cell-sub">{new Date(order.createdAt).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true })}</span>
                    </div></td>
                    <td><button className="order-details-action" aria-label={`Open details for order ${order.orderNumber}`} title="View order details" onClick={e => { e.stopPropagation(); setSelectedOrder(order); }}>•••</button></td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
            {visibleOrders.length === 0 && (
              <div className="table-empty"><strong>{search || priority !== 'all' ? 'No matching orders' : 'No orders in this queue'}</strong><p>{search || priority !== 'all' ? 'Try a different search or clear your filters.' : 'Choose another tab to see other orders.'}</p></div>
            )}
          </div>
          <div className="orders-pagination">
          <label>Rows per page <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))}>{[10, 25, 50].map(size => <option key={size} value={size}>{size}</option>)}</select></label>
          <span>{visibleOrders.length ? (currentPage - 1) * pageSize + 1 : 0}–{Math.min(currentPage * pageSize, visibleOrders.length)} of {visibleOrders.length}</span>
          <button aria-label="Previous page" disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)}>‹</button>
          <button aria-label="Next page" disabled={currentPage === pageCount} onClick={() => setPage(currentPage + 1)}>›</button>
          </div>
          </div>
          )}
        </div>
      </div>

      {/* Order Detail Modal */}
      {selectedOrder && (
        <OrderDetailModal
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
          onOrderUpdated={handleStatusUpdated}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
